import { useEffect } from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Message } from '@bwoty-web/styleguide';
import formatPrice from '@bwoty-web/price-formatter';
import * as CruiseListActions from '../redux/actions/cruiseListActions';
import SortPanel from '../components/cruiseList/SortPanel';
import CruiseListCard from '../components/cruiseList/CruiseListCard';
import MessageContainer from '../components/shared/message/MessageContainer';
import { sortedCruiseListSelector } from '../redux/selectors/cruiseList/sortSelectors';
import { filteredCruisePackagesSelector } from '../redux/selectors/cruiseList/filterSelectors';
import { urlUtils } from '../utils/url';
import {
  getCloudinarySources,
  getCloudinaryLogoSources,
} from '../utils/imageUtils';
import './cruiseListContainer.scss';
import FadeIn from '../utils/fadeIn';
import CruiseFilterPanel from '../components/cruiseList/filter/CruiseFilterPanel';
import { getProductsIncluded } from '../utils/mappers/productsIncluded';
import { texts, type } from '../assets/texts';
import { loaderImages } from '../assets/imageUrls/cruiseLoaderImages';
import GalleryLoader from '../components/shared/loaders/GalleryLoader';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import {
  getProductImpression,
  logProductClick,
} from '../utils/logUtils/CruiseListLogUtils';

const CruiseListContainer = (props) => {
  const {
    initialized,
    labels,
    actions,
    originalCruiseList,
    filteredAndSortedCruises,
    searchInformation,
    siteSettings,
    message,
    userId,
  } = props;

  const { imageDomain } = siteSettings;

  useEffect(() => {
    actions.init();
  }, [actions]);

  const hasSessionId = !!urlUtils.getSessionIdFromUrl();

  if (!initialized) {
    if (hasSessionId) {
      return (
        <LoaderHeading
          heading={texts[siteSettings.siteId].loaderHeadingShort}
          topMargin
        />
      );
    }

    return (
      <GalleryLoader
        siteSettings={siteSettings}
        imageList={loaderImages}
        heading={texts[siteSettings.siteId].loaderHeading}
        tips={texts[siteSettings.siteId].tips.filter(
          (tip) => tip.type === type.BOTH || tip.type === type.CRUISE,
        )}
      />
    );
  }

  const isZeroHit = originalCruiseList?.length <= 0;
  const filteredToZeroHits =
    !isZeroHit > 0 && filteredAndSortedCruises?.length === 0;

  const showPricePerPerson = searchInformation?.numberOfAdults === 2 && searchInformation?.numberOfChildren === 0;

  return (
    <FadeIn>
      <div className="iw-cruise-list-container">
        <MessageContainer
          message={message}
          user={!isZeroHit ? userId : ''}
          labels={labels}
          style={{ marginBottom: 20 }}
        />
        {!isZeroHit && (
          <>
            <SortPanel />
            <CruiseFilterPanel />
            <div className="iw-cruise-list">
              {filteredToZeroHits && (
                <Message
                  header={labels.zeroHitTooltipText}
                  messageType="info"
                />
              )}
              <div>
                {filteredAndSortedCruises.map((cruisePackage, index) => {
                  const logoImageSorces = getCloudinaryLogoSources(
                    imageDomain,
                    cruisePackage.cruise?.logoImage?.path,
                  );
                  const logoImage = {
                    sources: logoImageSorces,
                    alt: cruisePackage.cruise?.logoImage?.alt,
                  };
                  const imageRelativeUrl =
                    cruisePackage.cruise?.cruiseListImage?.path;
                  const imageContent = {
                    picture: {
                      alt: cruisePackage.cruise?.cruiseListImage?.alt,
                      sources: [
                        ...getCloudinarySources(
                          '(max-width: 370px)',
                          imageDomain,
                          imageRelativeUrl,
                          215,
                          380,
                        ),
                        ...getCloudinarySources(
                          '(max-width: 470px)',
                          imageDomain,
                          imageRelativeUrl,
                          585,
                          585,
                        ),
                        ...getCloudinarySources(
                          '(min-width: 768px)',
                          imageDomain,
                          imageRelativeUrl,
                          404,
                          585,
                        ),
                      ],
                    },
                    logoImage,
                    tags: [{ text: cruisePackage.cruise?.categoryText }],
                  };
                  const firstUsp = labels.cruiseNumberOfNightsText.replace(
                    '{numberOfNights}',
                    cruisePackage.cruise?.duration,
                  );

                  const plusMore =
                    cruisePackage.cruise?.itinerary?.filter(
                      (stop) => stop.resort !== '',
                    )?.length <= 4
                      ? ''
                      : labels.plusMoreText;
                  const itinerarySubTitleText = cruisePackage.cruise
                    ?.cruiseItinerarySubTitle
                    ? `${cruisePackage.cruise?.cruiseItinerarySubTitle} ${plusMore}`
                    : '';
                  const flightText = cruisePackage.transport
                    ? labels.flightIncludedText
                    : '';
                  const hotelNightsText =
                    cruisePackage.numberOfHotelNights > 1
                      ? `${cruisePackage.numberOfHotelNights} ${labels.manyHotelNightsText}`
                      : labels.oneHotelNightText;
                  const uspList = cruisePackage.cruise?.usps || [];
                  let cruiseSummaryLink = cruisePackage.summaryLink;
                  if (cruiseSummaryLink) {
                    if (!cruiseSummaryLink.startsWith('/')) {
                      cruiseSummaryLink = `/${cruiseSummaryLink}`;
                    }
                  }
                  const content = {
                    headerContent: {
                      titleText: cruisePackage.tripRuleName,
                      itinerarySubTitleText,
                      cruiseShipTitleText: cruisePackage.cruise?.cruiseShipName,
                      link: {
                        url: cruiseSummaryLink,
                        target: '_blank',
                      },
                      rating: cruisePackage.cruise?.cruiseShipRating,
                    },
                    bodyContent: {
                      uspList: [
                        cruisePackage.cruise?.duration > 0 && firstUsp,
                        ...uspList,
                      ],
                      texts: {
                        hotelNightsText:
                          cruisePackage.numberOfHotelNights > 0
                            ? `${flightText} + ${hotelNightsText}`
                            : flightText,
                        mealsText: cruisePackage.cruise?.mealText,
                      },
                    },
                    footerContent: {
                      texts: {
                        buttonText: labels?.chooseLabel,
                        packageInfoText: getProductsIncluded(
                          cruisePackage.packageTag,
                          labels,
                        ),
                        durAndDepText: cruisePackage.durAndDep,
                        noPriceText: labels?.noPriceText,
                      },
                      prices: {
                        price: cruisePackage?.totalPrice,
                        originalPrice:
                          cruisePackage.priceBreakDown
                            ?.totalPriceBeforeDiscount,
                        isDiscounted:
                          cruisePackage.priceBreakDown?.isDiscounted,
                        pricePerPerson: showPricePerPerson && cruisePackage.pricePerPax &&
                          formatPrice(cruisePackage.pricePerPax, siteSettings.siteId, true),
                        perPersonText: labels?.perPersonText,
                      },
                    },
                  };
                  return (
                    <CruiseListCard
                      key={uniqid('cruise-list-card-')}
                      siteId={siteSettings.siteId}
                      content={content}
                      imageContent={imageContent}
                      href={`/${cruisePackage.cruise?.cruiseDetailsLink}`}
                      position={index + 1}
                      onSelect={() =>
                        logProductClick(
                          getProductImpression(cruisePackage, index + 1),
                        )
                      }
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </FadeIn>
  );
};

CruiseListContainer.propTypes = {
  initialized: PropTypes.bool.isRequired,
  labels: PropTypes.shape({
    chooseLabel: PropTypes.string,
    noPriceText: PropTypes.string,
    cruiseNumberOfNightsText: PropTypes.string,
    plusMoreText: PropTypes.string,
    oneHotelNightText: PropTypes.string,
    manyHotelNightsText: PropTypes.string,
    zeroHitTooltipText: PropTypes.string.isRequired,
    flightIncludedText: PropTypes.string.isRequired,
    perPersonText: PropTypes.string.isRequired,
  }),
  actions: PropTypes.shape({
    init: PropTypes.func.isRequired,
  }).isRequired,
  siteSettings: PropTypes.shape({
    imageDomain: PropTypes.string.isRequired,
    siteId: PropTypes.number.isRequired,
  }).isRequired,
  originalCruiseList: PropTypes.arrayOf().isRequired,
  filteredAndSortedCruises: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  searchInformation: PropTypes.shape({
    numberOfAdults: PropTypes.number,
    numberOfChildren: PropTypes.number,
  }).isRequired,
  message: PropTypes.shape({
    title: PropTypes.string.isRequired,
    message: PropTypes.string,
    severity: PropTypes.string.isRequired,
  }),
  userId: PropTypes.string.isRequired,
};

CruiseListContainer.defaultProps = {
  labels: {},
  message: null,
};

const mapActions = (dispatch) => {
  return {
    actions: bindActionCreators({ ...CruiseListActions }, dispatch),
  };
};

const mapStateToProps = (state) => {
  const newState = state.toJS();
  const { isInspiration } = newState.common.siteSettings;
  const originalCruiseList = newState.cruises.cruises;

  return {
    labels: newState.labels,
    initialized: newState.cruises.initialized,
    originalCruiseList,
    filteredAndSortedCruises: isInspiration
      ? filteredCruisePackagesSelector(newState)
      : sortedCruiseListSelector(newState),
    searchInformation: newState.cruises.searchInformation,
    siteSettings: newState.common.siteSettings,
    message: newState.message,
    userId: newState.common.userId,
  };
};

export default connect(mapStateToProps, mapActions)(CruiseListContainer);
